import { useUserRole } from 'web-shared/hooks/useUserRole';
import { useUserCompanies } from 'web-shared/reducers/user-companies/hooks';
import { portalLevelPermissions } from 'web-shared/modules/Permissions/PortalLevelPermissions';
import PERMISSIONS_LIST from 'web-shared/modules/Permissions/permissions-list';

import { COMPANY_SETTING_NAME } from '../../../../global/modules/companies/api/company-settings/enums/company-settings';

export const useDeviceManagerAccess = () => {
  const { isAdminUser } = useUserRole();
  const { isCompanySettingEnabled } = useUserCompanies();

  const hasPermission = (permission: PERMISSIONS_LIST, portalId?: number) => {
    return portalLevelPermissions.isPermissionEnabled(permission, portalId);
  };

  /**
   * Determines if user has access to Device Management application
   */
  const hasDMAccess = () => {
    return isCompanySettingEnabled(COMPANY_SETTING_NAME.DEVICE_MANAGEMENT_ACCESS)
      && hasPermission(PERMISSIONS_LIST.device_management_access);
  };

  /**
   * Determines if user can create devices and tags in Device Management application
   */
  const hasDMCreateAccess = () => {
    return hasDMAccess() && hasPermission(PERMISSIONS_LIST.device_management_create);
  };

  /**
  * Determines if user can activate or deactivate devices and tags in Device Management application
  */
  const hasDMActivateDeactivateAccess = (portalId?: number) => {
    return hasDMAccess() && hasPermission(PERMISSIONS_LIST.device_management_activate_deactivate, portalId);
  };

  /**
   * Determines if user has access to vendors, device models, tag models and debug toggle on devices summary page
   */
  const hasDMDeviceDebugAccess = () => {
    return hasDMAccess() && hasPermission(PERMISSIONS_LIST.device_management_debug);
  };

  /**
   * Determines if user can edit devices and tags in Device Management application 
   */
  const hasDMEditAccess = (portalId: number) => {
    return hasDMAccess() && hasPermission(PERMISSIONS_LIST.device_management_edit, portalId);
  };

  /**
   * Determines if user can edit device models during device edit
   */
  const hasDMEditModelPermission = (portalId: number) => {
    return hasDMAccess() && hasPermission(PERMISSIONS_LIST.device_management_edit_model, portalId);
  };

  /**
   * Determines if user has access to import devices in Device Management application 
   */
  const hasDMImportAccces = () => {
    return hasDMAccess() && (hasDMCreateAccess() || hasPermission(PERMISSIONS_LIST.device_management_edit));
  };

  /**
   * Determines if user has access to device profile setup in Device Management application 
   */
  const hasDMDeviceProfileSetupAccess = () => {
    return hasDMAccess() && hasPermission(PERMISSIONS_LIST.device_profile_setup);
  };

  /**
   * Determines if user has access to configure device setting from external applications
   * This is used in Risk Monitor to control device settings access
   */
  const hasDMExternalDeviceControlAccess = (portalId: number) => {
    return hasDMAccess() && hasPermission(PERMISSIONS_LIST.sentinel_device_control, portalId);
  };

  const hasDMExportAccess = () => {
    return hasDMAccess() && (isAdminUser || hasPermission(PERMISSIONS_LIST.device_management_export_iot_device_data));
  };

  const hasDMGatewaysAccess = () => {
    return hasDMAccess() && isAdminUser && hasPermission(PERMISSIONS_LIST.device_management_gateways);
  };

  return {
    hasDMAccess,
    hasDMCreateAccess,
    hasDMActivateDeactivateAccess,
    hasDMDeviceDebugAccess,
    hasDMEditAccess,
    hasDMImportAccces,
    hasDMEditModelPermission,
    hasDMDeviceProfileSetupAccess,
    hasDMExternalDeviceControlAccess,
    hasDMExportAccess,
    hasDMGatewaysAccess,
  };
};
